import { createColumnHelper } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import {
  ListInlineRenameActions,
  ListInlineRenameField,
} from "@/modules/shell/ListInlineRename";
import type { WarehouseRow } from "@/shared/api/inventory";

const columnHelper = createColumnHelper<DeskTableFeatures, WarehouseRow>();

export type WarehouseColumnsOpts = {
  busy: boolean;
  editingId: string | null;
  editName: string;
  setEditName: (v: string) => void;
  onStartEdit: (row: WarehouseRow) => void;
  onCancelEdit: () => void;
  onSaveRename: () => void;
  onSetDefault: (row: WarehouseRow) => void;
  onDeactivate: (row: WarehouseRow) => void;
  onReactivate: (row: WarehouseRow) => void;
};

export function warehouseColumns(opts: WarehouseColumnsOpts) {
  return columnHelper.columns([
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 220,
      cell: ({ row }) => {
        const w = row.original;
        return (
          <ListInlineRenameField
            name={w.name}
            editing={opts.editingId === w.id}
            editValue={opts.editName}
            onEditValueChange={opts.setEditName}
            onSave={opts.onSaveRename}
            onCancel={opts.onCancelEdit}
            saving={opts.busy}
          />
        );
      },
    }),
    columnHelper.accessor("code", {
      id: "code",
      header: "Code",
      size: 100,
      cell: (info) => (
        <span className="font-mono text-sm">{info.getValue()}</span>
      ),
    }),
    columnHelper.display({
      id: "status",
      header: "Status",
      size: 140,
      enableSorting: false,
      cell: ({ row }) => {
        const w = row.original;
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            {w.isDefault ? <Badge variant="primary">default</Badge> : null}
            {w.deactivatedAt ? (
              <Badge variant="outline">deactivated</Badge>
            ) : null}
            {!w.isDefault && !w.deactivatedAt ? (
              <span className="text-xs text-[var(--color-muted)]">—</span>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 240,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => {
        const w = row.original;
        const editing = opts.editingId === w.id;
        if (editing) {
          return (
            <ListInlineRenameActions
              editing
              editValue={opts.editName}
              saving={opts.busy}
              onStartEdit={() => opts.onStartEdit(w)}
              onSave={opts.onSaveRename}
              onCancel={opts.onCancelEdit}
            />
          );
        }
        return (
          <div className="flex flex-wrap justify-end gap-1">
            <ListInlineRenameActions
              editing={false}
              editValue={opts.editName}
              startDisabled={opts.busy || w.deactivatedAt != null}
              onStartEdit={() => opts.onStartEdit(w)}
              onSave={opts.onSaveRename}
              onCancel={opts.onCancelEdit}
            />
            {!w.isDefault && w.deactivatedAt == null ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onSetDefault(w)}
              >
                Set default
              </Button>
            ) : null}
            {w.deactivatedAt == null && !w.isDefault ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={opts.busy}
                onClick={() => opts.onDeactivate(w)}
              >
                Deactivate
              </Button>
            ) : null}
            {w.deactivatedAt != null ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={opts.busy}
                onClick={() => opts.onReactivate(w)}
              >
                Reactivate
              </Button>
            ) : null}
          </div>
        );
      },
    }),
  ]);
}
